<script setup lang="ts">
import { aRgbToRgba } from '~/utils/formatters'

const props = defineProps<{
  modelValue: boolean
  popup?: any
  projectCode?: string
  exchangeSlug?: string
  traderData?: any
}>()

const matomo = useMatomo()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

let previousBodyOverflow = ''
let previousDocumentElementOverflow = ''
let hasLockedScroll = false

watch(
  () => props.modelValue,
  (visible) => {
    if (!import.meta.client) {
      return
    }

    if (visible) {
      previousBodyOverflow = document.body.style.overflow
      previousDocumentElementOverflow = document.documentElement.style.overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      hasLockedScroll = true
      return
    }

    if (hasLockedScroll) {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousDocumentElementOverflow
      hasLockedScroll = false
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (!import.meta.client || !hasLockedScroll) {
    return
  }

  document.body.style.overflow = previousBodyOverflow
  document.documentElement.style.overflow = previousDocumentElementOverflow
})

const closeDialog = (type: '关闭' | '我知道了') => {
  matomo.trackEvent('broker_title_pop', {
    af_type: type,
    name: props.traderData?.basicInfo?.shortName,
    code: props.exchangeSlug,
  })
  emit('update:modelValue', false)
}

const { t, localePath } = useAppI18n()

// /** 经营业务凭证图片页跳转地址。 */
// const certificateTo = computed(() => {
//   const projectCode = String(props.projectCode || '').trim()
//   const regulationId = String(props.popup?.regulationId || props.popup?.id || '').trim()

//   if (!projectCode) return ''

//   return localePath({
//     name: 'certificate',
//     query: {
//       from: 'regulation-card',
//       projectCode,
//       ...(regulationId ? { regulationId } : {}),
//     },
//   })
// })

const isImagePreviewVisible = ref(false)

const handleOpenSupportDocs = () => {
  isImagePreviewVisible.value = true
  matomo.trackEvent('broker_title_pop', {
    af_type: '证明文件',
    name: props.traderData?.basicInfo?.shortName,
    code: props.exchangeSlug,
  })
}
</script>

<template>
  <ElDialog
    :model-value="modelValue"
    width="540px"
    align-center
    append-to-body
    :show-close="false"
    :lock-scroll="false"
    class="business-popup-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div data-node-id="5667:80524" class="overflow-hidden rounded-[28px] bg-[#fff]">
      <div class="flex flex-col bg-[#fff] px-[32px] pt-[32px]">
        <div class="flex w-full items-center space-x-[8px] border-b-[0.5px] border-solid border-[#0515240f] pb-[16px]">
          <div
            class="min-w-0 flex-1 overflow-hidden text-[20px] leading-[28px] font-[600] break-words text-ellipsis whitespace-nowrap text-[#292a2e]"
          >
            {{ popup?.title }}
          </div>
          <div
            class="flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center"
            @click="closeDialog('关闭')"
          >
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/06/08/2af6f5ee-7ed0-4d4b-8dff-dcf5540ccb14.png"
              width="32"
              height="32"
              loading="eager"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center bg-[#fff] px-[32px] pt-[20px] pb-[32px]">
        <div class="flex w-full flex-col space-y-[16px]">
          <div
            v-if="popup?.tips?.text"
            class="w-full text-[16px] leading-[24px] font-[500] break-words"
            :style="{ color: aRgbToRgba(popup?.tips?.color || '#1F845A') }"
          >
            {{ popup.tips.text }}
          </div>

          <div class="flex w-full flex-col space-y-[12px] rounded-[8px] bg-[#f8f8f8] p-[16px]">
            <div class="w-full text-[13px] leading-[20px] font-[400] break-words text-[#292a2e]">
              {{ popup?.notes }}
            </div>

            <div v-if="popup?.regulators?.length" class="flex w-full flex-wrap items-start">
              <div
                v-for="regulator in popup.regulators"
                :key="regulator.text"
                class="mr-[16px] mb-[10px] flex shrink-0 items-center space-x-[8px] py-[4px] rtl:mr-[0] rtl:ml-[16px]"
              >
                <CommonImage
                  v-if="regulator.icon"
                  :src="regulator.icon"
                  class="rounded-[2px] object-cover"
                  width="24"
                  height="16"
                  loading="eager"
                />
                <span class="text-[14px] leading-[22px] font-[400] whitespace-nowrap text-[#292a2e]">
                  {{ regulator.text }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="popup?.supportDocs?.length" class="flex w-full items-center space-x-[4px]">
            <span class="text-[13px] leading-[20px] font-[400] whitespace-nowrap text-[#7d818a]">{{ t('83583') }}</span>
            <div
              class="flex cursor-pointer items-center space-x-[4px] text-[13px] leading-[20px] font-[400] whitespace-nowrap text-[#0d3f63]"
              @click="handleOpenSupportDocs"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/06/16/6c3d0f6d-9c33-469b-aa56-2c6dd46996be.png"
                width="16"
                height="16"
                loading="eager"
              />
              <span>{{ t('84541', [popup?.supportDocs?.length]) }}</span>
            </div>
          </div>
        </div>

        <div
          class="hover-mask-effect mt-[20px] flex h-[46px] w-[184px] cursor-pointer items-center justify-center rounded-[100px] bg-[#112d73] px-[64px] py-[12px] text-center text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#fff]"
          @click="closeDialog('我知道了')"
        >
          {{ t('83585') }}
        </div>
      </div>
    </div>
  </ElDialog>

  <!-- 公共大图图片预览弹窗 -->
  <CommonImagePreviewDialog v-model="isImagePreviewVisible" :images="popup?.supportDocs" />
</template>

<style scoped lang="less">
:global(.business-popup-dialog) {
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  padding: 0;
}

:global(.business-popup-dialog .el-dialog__header) {
  display: none;
  padding: 0;
  margin: 0;
}

:global(.business-popup-dialog .el-dialog__body) {
  padding: 0;
}

.hover-mask-effect {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.08);
    opacity: 0;
    transition: all 0.2s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  // 悬浮状态：显示白色 8% 蒙层
  &:hover::after {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.08);
  }

  // 按下状态：以 0.2 秒的平滑过渡切换并显示黑色 10% 蒙层
  &:active::after {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }
}
</style>
